import { useEffect, useMemo, useRef, useState } from 'react'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay'
import { Button } from '@/components/ui/button'
import { motion, AnimatePresence } from 'framer-motion'
import {
  useGetAccountManagersQuery,
  useGetAllSpacesQuery,
  useGetDeliveryHeadsQuery,
  useGetProjectsQuery,
  useGetProjectTlsQuery,
  useGetProjectFoldersQuery,
  useGetProjectStatusesQuery,
  useGetProjectDepartmentsQuery,
  useGetProjectTeamMembersQuery,
  useUpdateProjectMetaMutation,
  type Project,
  type ProjectTl,
} from '@/redux/service/project'
import {
  ArchiveIcon,
  Edit,
  Eye,
  RotateCcwIcon,
  X,
  Search,
  Filter,
  UserCog,
  UserCheck,
  Briefcase,
  Activity,
  PlusIcon,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  FileSignature,
  ListTree,
  FileQuestion,
  CalendarDays,
  FilterIcon,
  Check,
  ChevronDown,
  Loader2,
  Bookmark,
  Tag,
  Building2,
  FolderKanban,
  Users,
  StickyNote,
} from 'lucide-react'
import { CustomPagination } from '@/components/CustomPagination'
import ProjectModel from '@/components/ProjectModel'
import { Input } from '@/components/ui/input'
import ProjectDetails from '@/components/ProjectDetails'
import ProjectNotesDrawer from '@/components/ProjectNotesDrawer'

import { useDebounce } from '@/lib/useDebounce'
import { formatHours, formatSeconds, cn, formatCurrency } from '@/lib/utils'
import { getProjectSortValue, sortTableRows } from '@/lib/tableSort'
import { format } from 'date-fns'
import ReusableDateRangePicker, {
  type DateRange,
} from '@/components/ReusableDateRangePicker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import toast from 'react-hot-toast'
import { showLoadingToast, dismissLoadingToast } from '@/lib/toast'
import { Link } from 'react-router-dom'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ClickUpIcon } from '@/components/icons/ClickUpIcon'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import { useGetClientSelectOptionsQuery } from '@/redux/service/client'
import { FilterMultiSelect } from '@/components/ui/FilterMultiSelect'
import { FilterSearchMultiSelect } from '@/components/ui/FilterSearchMultiSelect'
import SavedFiltersPanel from '@/components/SavedFiltersPanel'
import { ProjectWorkspaceTabs } from '@/components/ProjectWorkspaceTabs'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'
interface Space {
  space_name: string
}

const SETUP_DEFAULT_VISIBLE_COLUMNS = [
  'project',
  'client',
  'project_billable_amount',
  'billable_profit',
  'gross_profit',
  'net_profit',
  'total_employee_cost',
  'overhead_cost',
  'fully_loaded_cost',
]

const DEFAULT_COLUMNS = [
  { key: 'project', label: 'Project' },
  { key: 'client', label: 'Client' },
  { key: 'total_hours', label: 'Total Hours' },
  { key: 'tracked_hours', label: 'Tracked Hours' },
  { key: 'remaining_hours', label: 'Remaining' },
  { key: 'billable', label: 'Billable' },
  { key: 'project_billable_amount', label: 'Revenue' },
  { key: 'billable_profit', label: 'Billable Profit' },
  { key: 'gross_profit', label: 'Gross Profit' },
  { key: 'net_profit', label: 'Net Profit' },
  { key: 'total_employee_cost', label: 'Direct Employee Cost' },
  { key: 'overhead_cost', label: 'Overhead Cost' },
  { key: 'fully_loaded_cost', label: 'Fully Loaded Cost' },
  { key: 'team', label: 'Team' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Type' },
  { key: 'assignee_name', label: 'Assignee Name' },
  { key: 'account_manager', label: 'Account Manager' },
  { key: 'delivery_head', label: 'Delivery Head' },
  { key: 'qa_status', label: 'QA Status' },
  { key: 'archived', label: 'Archived' },
  { key: 'space', label: 'Space' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'description', label: 'Description' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'end_date', label: 'End Date' },
  { key: 'created', label: 'Created' },
]

const FULL_FINANCE_ONLY_COLUMN_KEYS = [
  'billable_profit',
  'gross_profit',
  'net_profit',
  'total_employee_cost',
  'overhead_cost',
  'fully_loaded_cost',
] as const

function isFinanceColumnAllowed(key: string, isFullFinance: boolean): boolean {
  if (
    FULL_FINANCE_ONLY_COLUMN_KEYS.includes(
      key as (typeof FULL_FINANCE_ONLY_COLUMN_KEYS)[number]
    )
  ) {
    return isFullFinance
  }
  return true
}

type ProjectUserFilterOption = string | ProjectTl

function getUserFilterLabel(option: ProjectUserFilterOption): string {
  return typeof option === 'string' ? option : option.username
}

function getUserFilterKey(option: ProjectUserFilterOption): string {
  return typeof option === 'string' ? option : String(option.id)
}

export default function Projects({
  projectquery = useGetProjectsQuery,
  title = 'Projects',
}) {
  const isSetupPage = title === 'Project Setups'
  const {
    hasPageAccess,
    isTeamLeader,
    teamId,
    isLoading: isProfileLoading,
  } = useCheckPermission()
  const isFullFinance = hasPageAccess('finance')
  const hideTLFilters = isProfileLoading || isTeamLeader
  const [isDialogOpen, setIsDialogOpen] = useState<string | null>(null)
  const [page, setPage] = useState<number>(1)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [clientSearch, setClientSearch] = useState<string>('')
  const [editData, setEditData] = useState<Project | null>(null)
  const [notesDrawerProject, setNotesDrawerProject] = useState<Project | null>(
    null
  )
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const pendingEdgeRef = useRef<'start' | 'end' | null>(null)
  const [limit, setLimit] = useState<number>(100)
  const [spaceFilter, setSpaceFilter] = useState('Projects')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [projectArchive, setProjectArchive] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(1)
  const [clientSearchInput, setClientSearchInput] = useState('')
  const [isClientPopoverOpen, setIsClientPopoverOpen] = useState(false)
  const [folderFilter, setFolderFilter] = useState<string[]>([])
  const [projectStatusFilter, setProjectStatusFilter] = useState<string[]>([])
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([])
  const [teamMemberFilter, setTeamMemberFilter] = useState<string[]>([])
  const [teamMemberSearchInput, setTeamMemberSearchInput] = useState('')
  const [accountManager, setAccountManager] = useState<string>('all')
  const [deliveryHead, setDeliveryHead] = useState<string>('all')
  const [projectTl, setProjectTl] = useState<string>('all')
  const [filterCard, setFilterCard] = useState(false)
  const [isSavedFiltersPanelOpen, setIsSavedFiltersPanelOpen] = useState(false)
  const [savedFilterStartInSaveMode, setSavedFilterStartInSaveMode] =
    useState(false)
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })
  const [activityFilter, setActivityFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [columns, setColumns] = useState<{ key: string; label: string }[]>(
    () => {
      const savedOrder = localStorage.getItem('project-table-column-order')
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder)
          const fromSaved = parsedOrder
            .map((key: string) => DEFAULT_COLUMNS.find(c => c.key === key))
            .filter(Boolean) as { key: string; label: string }[]
          const savedKeys = new Set(fromSaved.map(column => column.key))
          const missingColumns = DEFAULT_COLUMNS.filter(
            column => !savedKeys.has(column.key)
          )
          return [...fromSaved, ...missingColumns]
        } catch {
          // Fallback to default
        }
      }
      return DEFAULT_COLUMNS
    }
  )

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const storageKey = isSetupPage
      ? 'project-setup-table-columns'
      : 'project-table-columns'
    const savedVisibility = localStorage.getItem(storageKey)
    if (savedVisibility) {
      try {
        return JSON.parse(savedVisibility)
      } catch {
        // Fallback to default
      }
    }
    if (isSetupPage) {
      return SETUP_DEFAULT_VISIBLE_COLUMNS
    }
    return DEFAULT_COLUMNS.map(col => col.key)
  })

  useEffect(() => {
    localStorage.setItem(
      'project-table-column-order',
      JSON.stringify(columns.map(c => c.key))
    )
  }, [columns])

  useEffect(() => {
    localStorage.setItem(
      isSetupPage ? 'project-setup-table-columns' : 'project-table-columns',
      JSON.stringify(selectedColumns)
    )
  }, [selectedColumns, isSetupPage])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedColumns.length > 1)
        : [...prev, key]
    )
  }

  const isColumnVisible = (key: string) =>
    selectedColumns.includes(key) && isFinanceColumnAllowed(key, isFullFinance)

  const SORT_KEY_MAP: Record<string, string> = {
    project: 'list_name',
    client: 'client_name',
    total_hours: 'total_hours',
    tracked_hours: 'tracked_duration_seconds',
    remaining_hours: 'remaining_hours',
    billable: 'billable_duration_seconds',
    non_billable: 'nonbillable_duration_seconds',
    project_billable_amount: 'project_billable_amount',
    billable_profit: 'billable_profit',
    gross_profit: 'gross_profit',
    net_profit: 'net_profit',
    total_employee_cost: 'total_employee_cost',
    overhead_cost: 'overhead_cost',
    fully_loaded_cost: 'fully_loaded_cost',
    team: 'folder_name',
    status: 'status',
    type: 'project_type',
    assignee_name: 'assignee_name',
    account_manager: 'account_manager',
    delivery_head: 'delivery_head',
    qa_status: 'qa_status',
    archived: 'archived',
    space: 'space_name',
    tasks: 'task_count',
    description: 'content',
    start_date: 'start_date',
    end_date: 'due_date',
    created: 'list_date_created',
  }

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-1 opacity-50' />
    if (order === 'ASC') return <ArrowUp className='w-3 h-3 ml-1' />
    return <ArrowDown className='w-3 h-3 ml-1' />
  }

  const updateProjectMeta = useUpdateProjectMetaMutation()[0]
  const {
    data: spacesData,
    isLoading: isSpacesLoading,
    isError: isSpacesError,
  } = useGetAllSpacesQuery(undefined, { skip: hideTLFilters })
  const debouncedSearch = useDebounce(searchQuery, 500)
  const debouncedClientDropdownSearch = useDebounce(clientSearchInput, 500)
  const debouncedClientFilter = useDebounce(clientSearch, 500)
  const debouncedTeamMemberSearch = useDebounce(teamMemberSearchInput, 500)
  const {
    data: foldersData,
    isLoading: isFoldersLoading,
    isError: isFoldersError,
  } = useGetProjectFoldersQuery(undefined, { skip: hideTLFilters })
  const {
    data: projectStatusesData,
    isLoading: isProjectStatusesLoading,
    isError: isProjectStatusesError,
  } = useGetProjectStatusesQuery(undefined, { skip: hideTLFilters })
  const {
    data: departmentsData,
    isLoading: isDepartmentsLoading,
    isError: isDepartmentsError,
  } = useGetProjectDepartmentsQuery(undefined, { skip: hideTLFilters })
  const {
    data: teamMembersData,
    isLoading: isTeamMembersLoading,
    isError: isTeamMembersError,
  } = useGetProjectTeamMembersQuery(
    {
      search: debouncedTeamMemberSearch,
      ...(departmentFilter.length > 0 ? { department: departmentFilter } : {}),
    },
    { skip: hideTLFilters, refetchOnMountOrArgChange: true }
  )
  const {
    data: accountManagersData,
    isLoading: isAccountManagersLoading,
    isError: isAccountManagersError,
  } = useGetAccountManagersQuery(
    { pending: projectquery !== useGetProjectsQuery },
    { skip: hideTLFilters }
  )
  const {
    data: deliveryHeadsData,
    isLoading: isDeliveryHeadsLoading,
    isError: isDeliveryHeadsError,
  } = useGetDeliveryHeadsQuery(
    { pending: projectquery !== useGetProjectsQuery },
    { skip: hideTLFilters }
  )
  const {
    data: projectTlsData,
    isLoading: isProjectTlsLoading,
    isError: isProjectTlsError,
  } = useGetProjectTlsQuery(
    { pending: projectquery !== useGetProjectsQuery },
    { skip: hideTLFilters }
  )

  const {
    data: clientsNames,
    isLoading: clientLoading,
    isFetching: clientFetching,
  } = useGetClientSelectOptionsQuery(
    {
      search: debouncedClientDropdownSearch,
      page: 1,
      limit: 50,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  )
  const { data, isLoading, isFetching, isError } = projectquery({
    page,
    limit,
    search: debouncedSearch,
    client: debouncedClientFilter,
    account_manager: accountManager === 'all' ? '' : accountManager,
    delivery_head: deliveryHead === 'all' ? '' : deliveryHead,
    project_tl: projectTl === 'all' ? '' : projectTl,
    space_name: hideTLFilters ? 'Projects' : spaceFilter,
    status: statusFilter === 'all' ? '' : statusFilter,
    ...(projectArchive ? { datatype: projectArchive } : {}),
    ...(dateRange?.from
      ? { startDate: format(dateRange.from, 'yyyy-MM-dd') }
      : {}),
    ...(dateRange?.to ? { endDate: format(dateRange.to, 'yyyy-MM-dd') } : {}),
    ...(activityFilter !== 'all' ? { activity: activityFilter } : {}),
    ...(sortBy ? { sort_by: sortBy } : {}),
    ...(order ? { order } : {}),
    ...(folderFilter.length > 0 ? { folder_name: folderFilter } : {}),
    ...(projectStatusFilter.length > 0
      ? { project_status: projectStatusFilter }
      : {}),
    ...(departmentFilter.length > 0 ? { department: departmentFilter } : {}),
    ...(teamMemberFilter.length > 0
      ? { team_member: teamMemberFilter.map(Number) }
      : {}),
  })

  const teamMemberOptions = useMemo(() => {
    const tlNames = new Set(
      (projectTlsData?.data ?? []).map(option =>
        getUserFilterLabel(option).trim().toLowerCase()
      )
    )
    return (teamMembersData?.data ?? [])
      .filter(member => !tlNames.has(member.user_name.trim().toLowerCase()))
      .map(member => ({
        value: String(member.user_id),
        label: member.user_name,
      }))
  }, [teamMembersData, projectTlsData])

  useEffect(() => {
    if (isTeamMembersLoading || teamMemberFilter.length === 0) return
    const validIds = new Set(teamMemberOptions.map(option => option.value))
    const pruned = teamMemberFilter.filter(id => validIds.has(id))
    if (pruned.length !== teamMemberFilter.length) {
      setTeamMemberFilter(pruned)
      setPage(1)
    }
  }, [teamMemberFilter, teamMemberOptions, isTeamMembersLoading])

  useEffect(() => {
    if (isFetching && !isLoading) {
      showLoadingToast('Loading projects...', 'projects-fetching-toast')
    } else {
      dismissLoadingToast('projects-fetching-toast')
    }
    return () => {
      dismissLoadingToast('projects-fetching-toast')
    }
  }, [isFetching, isLoading])

  const totalPages = data?.pagination?.totalPages ?? 0
  const totalrecords = data?.pagination?.totalRecords ?? 0
  const rows = useMemo<Project[]>(
    () => (isError ? [] : (data?.data ?? [])),
    [isError, data]
  )
  const sortedRows = useMemo(
    () => sortTableRows(rows, sortBy, order, getProjectSortValue),
    [rows, sortBy, order]
  )

  const handleEdit = ({ el, num }: { el: Project; num: string | null }) => {
    setEditData(el)
    setIsDialogOpen(num)
    const idx = sortedRows.findIndex(
      (r: Project) => r.project_id === el.project_id
    )
    setSelectedIndex(idx)
  }

  useEffect(() => {
    if (!pendingEdgeRef.current) return
    if (isLoading || isFetching) return
    if (!sortedRows || sortedRows.length === 0) {
      pendingEdgeRef.current = null
      return
    }
    const idx = pendingEdgeRef.current === 'end' ? 0 : sortedRows.length - 1
    setSelectedIndex(idx)
    setEditData(sortedRows[idx] ?? null)
    pendingEdgeRef.current = null
  }, [sortedRows, isLoading, isFetching])

  const totalRecords = data?.pagination?.totalRecords ?? sortedRows.length
  const globalIndex =
    selectedIndex < 0 ? 0 : (page - 1) * limit + selectedIndex + 1
  const hasPrev = selectedIndex > 0 || page > 1
  const hasNext = selectedIndex < sortedRows.length - 1 || page < totalPages

  const handlePrevProject = () => {
    if (selectedIndex > 0) {
      const i = selectedIndex - 1
      setSelectedIndex(i)
      setEditData(sortedRows[i] ?? null)
    } else if (page > 1) {
      pendingEdgeRef.current = 'start'
      setPage(page - 1)
    }
  }

  const handleNextProject = () => {
    if (selectedIndex < sortedRows.length - 1) {
      const i = selectedIndex + 1
      setSelectedIndex(i)
      setEditData(sortedRows[i] ?? null)
    } else if (page < totalPages) {
      pendingEdgeRef.current = 'end'
      setPage(page + 1)
    }
  }
  const handleAccountManagerChange = (value: string) => {
    setAccountManager(value)
    setPage(1)
  }
  const handleDeliveryHeadChange = (value: string) => {
    setDeliveryHead(value)
    setPage(1)
  }
  const handleProjectTlChange = (value: string) => {
    setProjectTl(value)
    setPage(1)
  }
  const handleArchive = async (project: Project) => {
    const pyload = {
      project_type: project.project_type,
      assignee_username: project.assignee_username,
      setup_completed: project.setup_completed,
      archived: !project.main_archived,
    }

    try {
      await updateProjectMeta({
        id: project.project_id,
        data: pyload,
      }).unwrap()
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Something went wrong')
    }
  }
  // const handleFullscreen = () => {
  //   document.documentElement.requestFullscreen()
  // }

  const handleClearFilters = () => {
    setSearchQuery('')
    setClientSearch('')
    setClientSearchInput('')
    setFolderFilter([])
    setProjectStatusFilter([])
    setDepartmentFilter([])
    setTeamMemberFilter([])
    setTeamMemberSearchInput('')
    setAccountManager('all')
    setDeliveryHead('all')
    setProjectTl('all')
    setSpaceFilter('Projects')
    setStatusFilter('all')
    setDateRange({ from: undefined, to: undefined })
    setActivityFilter('all')
    setSortBy(undefined)
    setOrder(undefined)
    setPage(1)
  }

  const hasActiveFilters =
    folderFilter.length > 0 ||
    projectStatusFilter.length > 0 ||
    departmentFilter.length > 0 ||
    teamMemberFilter.length > 0 ||
    accountManager !== 'all' ||
    deliveryHead !== 'all' ||
    projectTl !== 'all' ||
    spaceFilter !== 'Projects' ||
    statusFilter !== 'all' ||
    !!dateRange?.from ||
    activityFilter !== 'all' ||
    !!clientSearch

  const currentFiltersSnapshot: Record<string, unknown> = {
    search: searchQuery,
    space_name: spaceFilter,
    datatype: projectArchive ?? '',
    client: clientSearch,
    status: statusFilter === 'all' ? '' : statusFilter,
    account_manager: accountManager === 'all' ? '' : accountManager,
    delivery_head: deliveryHead === 'all' ? '' : deliveryHead,
    project_tl: projectTl === 'all' ? '' : projectTl,
    startDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
    endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
    activity: activityFilter === 'all' ? '' : activityFilter,
    project_status: projectStatusFilter,
    folder_name: folderFilter,
    department: departmentFilter,
    team_member: teamMemberFilter.map(Number),
    sort_by: sortBy ?? '',
    order: order ?? '',
  }

  const applyPreset = (filters: Record<string, unknown>) => {
    if (typeof filters['search'] === 'string') setSearchQuery(filters['search'])
    if (typeof filters['space_name'] === 'string')
      setSpaceFilter(filters['space_name'] || 'Projects')
    if (typeof filters['datatype'] === 'string')
      setProjectArchive(filters['datatype'] || null)
    if (typeof filters['client'] === 'string')
      setClientSearch(filters['client'])
    if (typeof filters['status'] === 'string')
      setStatusFilter(filters['status'] || 'all')
    if (typeof filters['account_manager'] === 'string')
      setAccountManager(filters['account_manager'] || 'all')
    if (typeof filters['delivery_head'] === 'string')
      setDeliveryHead(filters['delivery_head'] || 'all')
    if (typeof filters['project_tl'] === 'string')
      setProjectTl(filters['project_tl'] || 'all')
    if (typeof filters['startDate'] === 'string')
      setDateRange(prev => ({
        ...prev,
        from: filters['startDate']
          ? new Date(filters['startDate'] as string)
          : undefined,
      }))
    if (typeof filters['endDate'] === 'string')
      setDateRange(prev => ({
        ...prev,
        to: filters['endDate']
          ? new Date(filters['endDate'] as string)
          : undefined,
      }))
    if (typeof filters['activity'] === 'string')
      setActivityFilter(filters['activity'] || 'all')
    if (Array.isArray(filters['project_status']))
      setProjectStatusFilter(filters['project_status'] as string[])
    if (Array.isArray(filters['folder_name']))
      setFolderFilter(filters['folder_name'] as string[])
    if (Array.isArray(filters['department']))
      setDepartmentFilter(filters['department'] as string[])
    if (Array.isArray(filters['team_member'])) {
      setTeamMemberFilter(
        (filters['team_member'] as Array<string | number>).map(String)
      )
    }
    if (typeof filters['sort_by'] === 'string')
      setSortBy(filters['sort_by'] || undefined)
    if (typeof filters['order'] === 'string')
      setOrder((filters['order'] as 'ASC' | 'DESC') || undefined)
    setPage(1)
  }

  const visibleColumnsCount = useMemo(() => {
    const visibleCount = columns.filter(col => {
      if (!selectedColumns.includes(col.key)) return false
      if (!isFinanceColumnAllowed(col.key, isFullFinance)) return false
      if (
        isSetupPage &&
        [
          'total_hours',
          'tracked_hours',
          'remaining_hours',
          'billable',
          'non_billable',
        ].includes(col.key)
      )
        return false
      return true
    }).length
    return visibleCount + 1 // +1 for Actions
  }, [columns, selectedColumns, isSetupPage, isFullFinance])

  const formatListCreated = (value: string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—'
    const numeric = Number(value)
    const date =
      Number.isFinite(numeric) && String(numeric) === String(value)
        ? new Date(numeric)
        : new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return format(date, 'dd MMM yyyy, HH:mm')
  }

  const formatProjectDate = (value: string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return format(date, 'dd MMM yyyy')
  }

  let tableBodyContent
  if (isLoading && sortedRows.length === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={visibleColumnsCount} className='py-8' />
      </TableRow>
    )
  } else if (isError || sortedRows.length === 0) {
    tableBodyContent = null
  } else {
    const linkIconFor = (type: string): React.ReactNode => {
      switch (type) {
        case 'clickup':
          return <ClickUpIcon className='h-3 w-3' />
        case 'schedule':
          return <CalendarDays className='h-3 w-3' />
        case 'contract':
          return <FileSignature className='h-3 w-3' />
        case 'wbs':
          return <ListTree className='h-3 w-3' />
        case 'why_doc':
          return <FileQuestion className='h-3 w-3' />
        default:
          return <ExternalLink className='h-3 w-3' />
      }
    }
    tableBodyContent = sortedRows?.map((project: Project) => {
      const baseLinks = project?.links ?? []
      const hasSchedule = baseLinks.some(
        l => l.type === 'schedule' || l.url === project?.url
      )
      const projectLinks =
        project?.url && !hasSchedule
          ? [
              ...baseLinks,
              {
                type: 'schedule',
                label: 'Schedule',
                url: project.url,
              },
            ]
          : baseLinks
      return (
        <TableRow
          key={project.list_id}
          className='group border-b border-border hover:bg-muted/30 transition-colors'
        >
          {columns.map(column => {
            if (!isColumnVisible(column.key)) return null
            if (
              isSetupPage &&
              [
                'total_hours',
                'tracked_hours',
                'remaining_hours',
                'billable',
                'non_billable',
              ].includes(column.key)
            )
              return null

            switch (column.key) {
              case 'project':
                return (
                  <TableCell
                    key={column.key}
                    className='sticky left-0 z-20 bg-background w-[420px] min-w-[420px] max-w-[420px] font-medium align-middle'
                  >
                    <div className='min-w-0 flex flex-col justify-center'>
                      <div className='truncate font-medium'>
                        {project?.list_name ?? '—'}
                      </div>
                      <div
                        className='h-0 group-hover:h-5 opacity-0 group-hover:opacity-100 transition-all duration-150 overflow-hidden'
                        aria-hidden={projectLinks.length === 0}
                      >
                        {projectLinks.length > 0 && (
                          <div className='flex items-center flex-nowrap gap-x-3 text-[11px] whitespace-nowrap overflow-hidden mt-1'>
                            {projectLinks.map(link => (
                              <a
                                key={link.type}
                                href={link.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 text-muted-foreground hover:text-primary hover:underline transition-colors'
                                title={`Open ${link.label} in new tab`}
                              >
                                {linkIconFor(link.type)}
                                <span>{link.label}</span>
                                <ExternalLink className='h-2.5 w-2.5 opacity-60' />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                )
              case 'client':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {project?.client_name && project?.client_link ? (
                      <a
                        href={project.client_link}
                        target='_blank'
                        rel='noopener noreferrer'
                        className=' hover:text-primary  hover:underline transition-colors inline-flex items-center gap-1'
                        title={`Open ${project.client_name} in Attio`}
                      >
                        {project.client_name}
                        <ExternalLink className='h-3 w-3 opacity-60' />
                      </a>
                    ) : (
                      (project?.client_name ?? '—')
                    )}
                  </TableCell>
                )
              case 'total_hours':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatHours(project.total_hours)}
                  </TableCell>
                )
              case 'tracked_hours':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatSeconds(
                      Number(project.billable_duration_seconds ?? 0) +
                        Number(project.nonbillable_duration_seconds ?? 0)
                    )}
                  </TableCell>
                )
              case 'remaining_hours':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatHours(project.remaining_hours)}
                  </TableCell>
                )
              case 'billable':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatSeconds(project.billable_duration_seconds)}
                  </TableCell>
                )
              case 'non_billable':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatSeconds(project.nonbillable_duration_seconds)}
                  </TableCell>
                )
              case 'project_billable_amount':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatCurrency(project.billable_revenue)}
                  </TableCell>
                )
              case 'billable_profit':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatCurrency(project.billable_profit)}
                  </TableCell>
                )
              case 'gross_profit':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatCurrency(project.gross_profit)}
                  </TableCell>
                )
              case 'net_profit':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatCurrency(project.net_profit)}
                  </TableCell>
                )
              case 'total_employee_cost':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatCurrency(project.total_employee_cost)}
                  </TableCell>
                )
              case 'overhead_cost':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatCurrency(project.overhead_cost)}
                  </TableCell>
                )
              case 'fully_loaded_cost':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {formatCurrency(project.fully_loaded_cost)}
                  </TableCell>
                )
              case 'team':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {project?.folder_name ?? '—'}
                  </TableCell>
                )
              case 'status':
                return (
                  <TableCell key={column.key} className='truncate capitalize'>
                    {project?.status ?? '—'}
                  </TableCell>
                )
              case 'type':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {project?.contract_link ? (
                      <Link
                        to={project?.contract_link}
                        target='_blank'
                        rel='noopener noreferrer'
                        className=' hover:text-primary  hover:underline transition-colors inline-flex items-center gap-1'
                        title={`Open Contract Link in new tab`}
                      >
                        {project?.project_type ?? '—'}
                        <ExternalLink className='h-3 w-3 opacity-60' />
                      </Link>
                    ) : (
                      (project?.project_type ?? '—')
                    )}
                  </TableCell>
                )
              case 'assignee_name':
                return (
                  <TableCell
                    key={column.key}
                    className={`truncate ${
                      project?.assignee_name ? 'text-start' : 'text-center'
                    }`}
                  >
                    {project.assignee_name ?? '—'}
                  </TableCell>
                )
              case 'account_manager':
                return (
                  <TableCell
                    key={column.key}
                    className={`truncate ${
                      project?.account_manager ? 'text-start' : 'text-center'
                    }`}
                  >
                    {project.account_manager ?? '—'}
                  </TableCell>
                )
              case 'delivery_head':
                return (
                  <TableCell
                    key={column.key}
                    className={`truncate ${
                      project?.delivery_head ? 'text-start' : 'text-center'
                    }`}
                  >
                    {project.delivery_head ?? '—'}
                  </TableCell>
                )
              case 'qa_status':
                return (
                  <TableCell
                    key={column.key}
                    className={`truncate capitalize ${
                      project?.qa_status ? 'text-start' : 'text-center'
                    }`}
                  >
                    {project.qa_status ?? '—'}
                  </TableCell>
                )
              case 'archived':
                return (
                  <TableCell key={column.key} className='truncate text-center'>
                    {project.archived === 1 ? (
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20'>
                        Archived
                      </span>
                    ) : (
                      <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600 border border-green-500/20'>
                        Not Archived
                      </span>
                    )}
                  </TableCell>
                )
              case 'space':
                return (
                  <TableCell key={column.key} className='truncate text-center'>
                    {project.space_name ?? '—'}
                  </TableCell>
                )
              case 'tasks':
                return (
                  <TableCell key={column.key} className='text-center'>
                    {project.task_count ?? 0}
                  </TableCell>
                )
              case 'description':
                return (
                  <TableCell
                    key={column.key}
                    className='truncate'
                    title={project?.content}
                  >
                    {project?.content
                      ? project.content.length > 50
                        ? `${project.content.slice(0, 50)}...`
                        : project.content
                      : '—'}
                  </TableCell>
                )
              case 'created':
                return (
                  <TableCell
                    key={column.key}
                    className='truncate text-center whitespace-nowrap'
                  >
                    {formatListCreated(project?.list_date_created)}
                  </TableCell>
                )
              case 'start_date':
                return (
                  <TableCell
                    key={column.key}
                    className='truncate text-center whitespace-nowrap'
                  >
                    {formatProjectDate(project?.start_date)}
                  </TableCell>
                )
              case 'end_date':
                return (
                  <TableCell
                    key={column.key}
                    className='truncate text-center whitespace-nowrap'
                  >
                    {formatProjectDate(project?.due_date)}
                  </TableCell>
                )
              default:
                return null
            }
          })}
          <TableCell className='sticky right-0 z-10 border-s bg-background/95 backdrop-blur-sm flex items-center gap-2  shadow-[-8px_0_12px_-12px_rgba(0,0,0,0.35)] opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
            {(hasPageAccess('finance') || hasPageAccess('mini_finance')) && (
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
                onClick={() => handleEdit({ el: project, num: 'finance' })}
              >
                <PlusIcon />
              </Button>
            )}
            <Button
              variant='outline'
              size='sm'
              className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
              onClick={() => handleEdit({ el: project, num: '2' })}
            >
              <Eye />
            </Button>
            <Button
              variant='outline'
              size='sm'
              className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
              onClick={() => handleEdit({ el: project, num: '1' })}
            >
              <Edit />
            </Button>
            {!isSetupPage && (
              <Button
                variant='outline'
                size='sm'
                className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
                onClick={() => setNotesDrawerProject(project)}
              >
                <StickyNote />
              </Button>
            )}
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
                >
                  {project.main_archived ? <RotateCcwIcon /> : <ArchiveIcon />}
                </Button>
              </DialogTrigger>

              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {project.main_archived
                      ? 'Restore Project?'
                      : 'Archive Project?'}
                  </DialogTitle>
                  <DialogDescription>
                    {project.main_archived
                      ? 'This project will be restored and moved back to Active Projects.'
                      : 'This project will be archived and hidden from Active Projects.'}
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className='gap-2'>
                  <DialogClose asChild>
                    <Button variant='outline'>Cancel</Button>
                  </DialogClose>

                  <DialogClose asChild>
                    <Button
                      variant={
                        project.main_archived ? 'default' : 'destructive'
                      }
                      onClick={() => handleArchive(project)}
                    >
                      {project.main_archived ? 'Restore' : 'Archive'}
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TableCell>
        </TableRow>
      )
    })
  }

  const showEmptyState =
    !isLoading && !isFetching && (isError || sortedRows.length === 0)

  return (
    <div className='h-full flex flex-col space-y-4 overflow-hidden min-w-0'>
      <div className='shrink-0 space-y-4 flex-wrap'>
        <div className='flex flex-col space-y-6'>
          <div className='flex items-center justify-between'>
            {/* <Link
              to={'/sheet'}
              onClick={handleFullscreen}
              className='bg-black text-white px-4 py-2 rounded-lg font-bold shadow-md hover:opacity-90 transition-all flex items-center gap-2'
            >
              <Plus className='w-5 h-5' /> Bulk Project Setup
            </Link> */}
          </div>

          {/* Filter Section */}
        </div>

        <div className='flex flex-col flex-wrap lg:flex-row lg:items-center justify-between border-b border-border pb-3 mb-4 gap-4 min-w-0 w-full'>
          <ProjectWorkspaceTabs hidePendingSetup={hideTLFilters} />

          <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full min-w-0 lg:w-auto'>
            <div className='w-full min-w-0 sm:w-44 shrink-0'>
              <Select
                value={
                  isSetupPage
                    ? activeTab === 1
                      ? 'pending-setup'
                      : activeTab === 2
                        ? 'archived'
                        : 'clickup'
                    : activeTab === 1
                      ? 'active'
                      : activeTab === 2
                        ? 'archived'
                        : 'clickup'
                }
                onValueChange={value => {
                  if (value === 'active' || value === 'pending-setup') {
                    setActiveTab(1)
                    setProjectArchive(null)
                  } else if (value === 'archived') {
                    setActiveTab(2)
                    setProjectArchive('archived')
                  } else {
                    setActiveTab(3)
                    setProjectArchive('clickup')
                  }
                  setPage(1)
                }}
              >
                <SelectTrigger className='h-9 w-full sm:w-44 shrink-0 border-border bg-card hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSetupPage ? (
                    <SelectItem value='pending-setup'>Pending Setup</SelectItem>
                  ) : (
                    <SelectItem value='active'>Active Projects</SelectItem>
                  )}
                  <SelectItem value='archived'>App Archives</SelectItem>
                  <SelectItem value='clickup'>Clickup Archives</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='relative w-full min-w-0 sm:w-48 shrink-0'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
              <Input
                placeholder='Search projects by name...'
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                className='pl-10 pr-8 h-9 border-border text-foreground focus:border-primary transition-all rounded-lg text-xs bg-card'
              />
              {searchQuery && (
                <button
                  type='button'
                  onClick={() => {
                    setSearchQuery('')
                    setPage(1)
                  }}
                  className='absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-foreground'
                >
                  <X className='w-3.5 h-3.5' />
                </button>
              )}
            </div>
            <div className='relative w-full min-w-0 sm:w-40 shrink-0'>
              <Popover
                open={isClientPopoverOpen}
                onOpenChange={setIsClientPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant='secondary'
                    role='combobox'
                    aria-expanded={isClientPopoverOpen}
                    className='w-full h-9 justify-between border-border bg-card hover:!bg-transparent hover:text-foreground text-foreground transition-all rounded-lg px-3 text-xs'
                  >
                    <span className='truncate text-xs font-normal'>
                      {clientSearch || 'Search by client...'}
                    </span>
                    {clientSearch ? (
                      <span
                        role='button'
                        tabIndex={0}
                        onClick={e => {
                          e.stopPropagation()
                          setClientSearch('')
                          setPage(1)
                        }}
                        className='ml-2 h-4 w-4 shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground'
                      >
                        <X className='h-3.5 w-3.5' />
                      </span>
                    ) : (
                      <ChevronDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-[var(--radix-popover-trigger-width)] p-0 bg-popover border-border shadow-xl rounded-xl z-[100] '
                  align='start'
                >
                  <div className='flex flex-col h-full max-h-[300px]'>
                    <div className='flex items-center border-b border-border p-2'>
                      <div className='flex items-center relative bg-muted rounded-md py-1 flex-1'>
                        <Search className=' absolute h-4 w-4 shrink-0 text-muted-foreground ms-2' />
                        <Input
                          placeholder='Search clients...'
                          value={clientSearchInput}
                          onChange={e => setClientSearchInput(e.target.value)}
                          className='h-8 w-full border-none  text-sm ps-7'
                          autoFocus
                        />
                      </div>
                      {clientSearchInput && (
                        <Button
                          variant='ghost'
                          size='icon'
                          className='h-6 w-6 hover:bg-muted'
                          onClick={() => setClientSearchInput('')}
                        >
                          <X className='h-3 w-3' />
                        </Button>
                      )}
                    </div>
                    <div className='overflow-y-auto py-1 '>
                      {clientLoading || clientFetching ? (
                        <div className='flex items-center justify-center py-6 px-4'>
                          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground mr-2' />
                          <span className='text-xs text-muted-foreground'>
                            Loading clients...
                          </span>
                        </div>
                      ) : clientsNames?.data?.length === 0 ? (
                        <div className='py-6 text-center text-xs text-muted-foreground'>
                          No clients found
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setClientSearch('')
                              setClientSearchInput('')
                              setPage(1)
                              setIsClientPopoverOpen(false)
                            }}
                            className='w-full flex items-center px-3 py-2 text-xs hover:bg-accent transition-colors'
                          >
                            <span className='flex-1 text-left'>
                              All Clients
                            </span>
                          </button>
                          {clientsNames?.data?.map(client => (
                            <button
                              key={client.name}
                              onClick={() => {
                                setClientSearch(client.name)
                                setClientSearchInput('')
                                setPage(1)
                                setIsClientPopoverOpen(false)
                              }}
                              className={`w-full flex items-center px-3 py-2 text-xs hover:bg-accent transition-colors ${
                                clientSearch === client.name
                                  ? 'bg-accent/50 font-medium text-primary'
                                  : 'text-foreground'
                              }`}
                            >
                              <span className='flex-1 text-left truncate'>
                                {client.name ?? '—'}
                              </span>
                              {clientSearch === client.name && (
                                <Check className='h-3 w-3 text-primary ml-2' />
                              )}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className='flex items-center gap-3 w-full min-w-0 max-w-full sm:w-auto shrink-0'>
              <Button
                onClick={() => setFilterCard(prev => !prev)}
                variant='secondary'
                size='sm'
                className='flex flex-1 sm:flex-none gap-2 h-9 text-xs border-border justify-center'
              >
                <FilterIcon className='w-3 h-3' />
              </Button>
              <ColumnVisibilityPopover
                columns={columns}
                isColumnVisible={isColumnVisible}
                onToggle={toggleColumn}
                onReset={() => {
                  setColumns(DEFAULT_COLUMNS)
                  setSelectedColumns(
                    isSetupPage
                      ? SETUP_DEFAULT_VISIBLE_COLUMNS.filter(key =>
                          isFinanceColumnAllowed(key, isFullFinance)
                        )
                      : DEFAULT_COLUMNS.filter(col =>
                          isFinanceColumnAllowed(col.key, isFullFinance)
                        ).map(col => col.key)
                  )
                }}
                onDragEnd={handleDragEnd}
                sensors={sensors}
                filterColumn={column =>
                  isFinanceColumnAllowed(column.key, isFullFinance)
                }
                triggerClassName='flex flex-1 sm:flex-none gap-2 h-9 text-xs border-border bg-card hover:!bg-transparent hover:text-foreground text-foreground transition-all rounded-lg px-3 justify-center'
              />
            </div>
          </div>
        </div>

        <AnimatePresence>
          {filterCard && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className='overflow-hidden mb-6'
            >
              <div className='border border-border rounded-xl bg-card p-6 shadow-sm space-y-4'>
                <div className='flex items-center justify-between px-1'>
                  <div className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70'>
                    Advanced Filters
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => {
                        setSavedFilterStartInSaveMode(false)
                        setIsSavedFiltersPanelOpen(true)
                      }}
                      className='h-7 px-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center gap-1.5'
                    >
                      <Bookmark className='w-3 h-3' /> Saved Filters
                    </Button>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => {
                        setSavedFilterStartInSaveMode(true)
                        setIsSavedFiltersPanelOpen(true)
                      }}
                      disabled={!hasActiveFilters}
                      className='h-7 px-2 text-[10px] uppercase tracking-wider font-bold text-primary/70 hover:text-primary hover:bg-primary/10 transition-colors flex items-center gap-1.5 disabled:opacity-40'
                    >
                      <Bookmark className='w-3 h-3' /> Save Filter
                    </Button>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={handleClearFilters}
                      className='h-7 px-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-1.5'
                    >
                      <X className='w-3 h-3' /> Reset All
                    </Button>
                  </div>
                </div>

                <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2'>
                  {!hideTLFilters && (
                    <Select
                      value={spaceFilter}
                      onValueChange={value => {
                        setSpaceFilter(value === 'all' ? '' : value)
                        setPage(1)
                      }}
                      disabled={isSpacesLoading || isSpacesError}
                    >
                      <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <Filter className='w-3 h-3 text-muted-foreground shrink-0' />
                          <span className='truncate text-left'>
                            <SelectValue
                              placeholder={
                                isSpacesLoading
                                  ? 'Loading...'
                                  : isSpacesError
                                    ? 'Error'
                                    : 'All Spaces'
                              }
                            />
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All Spaces</SelectItem>
                        {spacesData?.data?.map((space: Space) => (
                          <SelectItem
                            key={space?.space_name}
                            value={space?.space_name}
                          >
                            {space.space_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {!hideTLFilters && (
                    <FilterMultiSelect
                      groups={foldersData?.data ?? []}
                      selected={folderFilter}
                      onChange={v => {
                        setFolderFilter(v)
                        setPage(1)
                      }}
                      placeholder='Folder'
                      icon={<FolderKanban className='w-3 h-3' />}
                      isLoading={isFoldersLoading}
                      isError={isFoldersError}
                    />
                  )}

                  {!hideTLFilters && (
                    <FilterMultiSelect
                      options={projectStatusesData?.data ?? []}
                      selected={projectStatusFilter}
                      onChange={v => {
                        setProjectStatusFilter(v)
                        setPage(1)
                      }}
                      placeholder='Project Status'
                      icon={<Tag className='w-3 h-3' />}
                      isLoading={isProjectStatusesLoading}
                      isError={isProjectStatusesError}
                    />
                  )}

                  {!hideTLFilters && (
                    <FilterMultiSelect
                      options={departmentsData?.data ?? []}
                      selected={departmentFilter}
                      onChange={v => {
                        setDepartmentFilter(v)
                        setPage(1)
                      }}
                      placeholder='Department'
                      icon={<Building2 className='w-3 h-3' />}
                      isLoading={isDepartmentsLoading}
                      isError={isDepartmentsError}
                    />
                  )}

                  <Select
                    value={statusFilter}
                    onValueChange={value => {
                      setStatusFilter(value)
                      setPage(1)
                    }}
                  >
                    <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                      <div className='flex items-center gap-2 min-w-0'>
                        <Activity className='w-3 h-3 text-muted-foreground shrink-0' />
                        <span className='truncate text-left'>
                          <SelectValue placeholder='All Types' />
                        </span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All Types</SelectItem>
                      <SelectItem value='Upwork Tracking'>
                        Upwork Tracking
                      </SelectItem>
                      <SelectItem value='Outside Fix'>Outside Fix</SelectItem>
                      <SelectItem value='Internal'>Internal</SelectItem>
                      <SelectItem value='Upwork Fix'>Upwork Fix</SelectItem>
                    </SelectContent>
                  </Select>

                  {!hideTLFilters && (
                    <Select
                      value={accountManager}
                      onValueChange={handleAccountManagerChange}
                      disabled={
                        isAccountManagersLoading || isAccountManagersError
                      }
                    >
                      <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <UserCog className='w-3 h-3 text-muted-foreground shrink-0' />
                          <span className='truncate text-left'>
                            <SelectValue
                              placeholder={
                                isAccountManagersLoading
                                  ? 'Loading...'
                                  : isAccountManagersError
                                    ? 'Error'
                                    : 'Account Manager'
                              }
                            />
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent className='max-h-[300px]'>
                        <SelectItem value='all'>
                          All Account Managers
                        </SelectItem>
                        {!isAccountManagersLoading &&
                          !isAccountManagersError &&
                          accountManagersData?.data?.map(option => {
                            const label = getUserFilterLabel(option)
                            return (
                              <SelectItem
                                key={getUserFilterKey(option)}
                                value={label}
                              >
                                {label}
                              </SelectItem>
                            )
                          })}
                      </SelectContent>
                    </Select>
                  )}

                  {!hideTLFilters && (
                    <Select
                      value={deliveryHead}
                      onValueChange={handleDeliveryHeadChange}
                      disabled={isDeliveryHeadsLoading || isDeliveryHeadsError}
                    >
                      <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <UserCheck className='w-3 h-3 text-muted-foreground shrink-0' />
                          <span className='truncate text-left'>
                            <SelectValue
                              placeholder={
                                isDeliveryHeadsLoading
                                  ? 'Loading...'
                                  : isDeliveryHeadsError
                                    ? 'Error'
                                    : 'Delivery Head'
                              }
                            />
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent className='max-h-[300px]'>
                        <SelectItem value='all'>All Delivery Heads</SelectItem>
                        {!isDeliveryHeadsLoading &&
                          !isDeliveryHeadsError &&
                          deliveryHeadsData?.data?.map(option => {
                            const label = getUserFilterLabel(option)
                            return (
                              <SelectItem
                                key={getUserFilterKey(option)}
                                value={label}
                              >
                                {label}
                              </SelectItem>
                            )
                          })}
                      </SelectContent>
                    </Select>
                  )}

                  {!hideTLFilters && (
                    <Select
                      value={projectTl}
                      onValueChange={handleProjectTlChange}
                      disabled={isProjectTlsLoading || isProjectTlsError}
                    >
                      <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                        <div className='flex items-center gap-2 min-w-0'>
                          <Briefcase className='w-3 h-3 text-muted-foreground shrink-0' />
                          <span className='truncate text-left'>
                            <SelectValue
                              placeholder={
                                isProjectTlsLoading
                                  ? 'Loading...'
                                  : isProjectTlsError
                                    ? 'Error'
                                    : 'Project TL'
                              }
                            />
                          </span>
                        </div>
                      </SelectTrigger>
                      <SelectContent className='max-h-[300px]'>
                        <SelectItem value='all'>All Project TLs</SelectItem>
                        {!isProjectTlsLoading &&
                          !isProjectTlsError &&
                          projectTlsData?.data?.map(option => {
                            const key = getUserFilterKey(option)
                            return (
                              <SelectItem key={key} value={key}>
                                {getUserFilterLabel(option)}
                              </SelectItem>
                            )
                          })}
                      </SelectContent>
                    </Select>
                  )}
                  {!hideTLFilters && (
                    <FilterSearchMultiSelect
                      options={teamMemberOptions}
                      selected={teamMemberFilter}
                      onChange={v => {
                        setTeamMemberFilter(v)
                        setPage(1)
                      }}
                      onSearchChange={setTeamMemberSearchInput}
                      searchValue={teamMemberSearchInput}
                      searchPlaceholder='Search team members...'
                      placeholder='Team Member'
                      icon={<Users className='w-3 h-3' />}
                      isLoading={isTeamMembersLoading}
                      isError={isTeamMembersError}
                    />
                  )}

                  <ReusableDateRangePicker
                    onSelect={val => {
                      setDateRange(val)
                      setPage(1)
                    }}
                    className='w-full '
                  />
                  <Select
                    value={activityFilter}
                    onValueChange={value => {
                      setActivityFilter(value)
                      setPage(1)
                    }}
                    disabled={!dateRange?.from && !dateRange?.to}
                  >
                    <SelectTrigger className='h-9 w-full border-border  hover:bg-accent/30 transition-colors text-xs rounded-lg'>
                      <div className='flex items-center gap-2 min-w-0'>
                        <Activity className='w-3 h-3 text-muted-foreground shrink-0' />
                        <span className='truncate text-left'>
                          <SelectValue placeholder='All Activity' />
                        </span>
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>All Activity</SelectItem>
                      <SelectItem value='active'>Active</SelectItem>
                      <SelectItem value='inactive'>Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className='flex-1 relative overflow-hidden border border-border rounded-sm'>
        <Table className='min-w-500 border-none '>
          <TableHeader>
            <TableRow className=' border-border'>
              {columns.map(column => {
                if (!isColumnVisible(column.key)) return null
                if (
                  isSetupPage &&
                  [
                    'total_hours',
                    'tracked_hours',
                    'remaining_hours',
                    'billable',
                    'non_billable',
                  ].includes(column.key)
                )
                  return null

                const sortKey = SORT_KEY_MAP[column.key]
                const isSticky = column.key === 'project'

                return (
                  <TableHead
                    key={column.key}
                    className={cn(
                      'font-medium truncate',
                      isSticky &&
                        'sticky left-0 z-30 bg-background w-[420px] min-w-[420px] max-w-[420px]',
                      sortKey && 'cursor-pointer select-none'
                    )}
                    onClick={sortKey ? () => handleSort(sortKey) : undefined}
                  >
                    <div
                      className={cn(
                        'flex items-center',
                        [
                          'total_hours',
                          'tracked_hours',
                          'remaining_hours',
                          'billable',
                          'non_billable',
                          'project_billable_amount',
                          'billable_profit',
                          'gross_profit',
                          'net_profit',
                          'total_employee_cost',
                          'overhead_cost',
                          'fully_loaded_cost',
                          'assignee',
                          'assignee_name',
                          'account_manager',
                          'delivery_head',
                          'qa_status',
                          'archived',
                          'space',
                          'tasks',
                          'start_date',
                          'end_date',
                          'created',
                        ].includes(column.key) && 'justify-center'
                      )}
                    >
                      {column.label} {sortKey && getSortIcon(sortKey)}
                    </div>
                  </TableHead>
                )
              })}
              <TableHead className=' font-medium truncate'>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{tableBodyContent}</TableBody>
        </Table>
        {(isLoading || isFetching) && (
          <TableLoadingOverlay label='Loading projects...' />
        )}
        {showEmptyState && (
          <div className='absolute inset-0 z-10 flex items-center justify-center pointer-events-none'>
            {isTeamLeader && !teamId ? (
              <div className='flex flex-col items-center gap-1 text-center px-4'>
                <p className='text-muted-foreground text-sm font-medium'>
                  Your account isn't assigned to a team yet
                </p>
                <p className='text-muted-foreground text-xs'>
                  Ask an admin to add you as a member of your team.
                </p>
              </div>
            ) : (
              <p
                className={cn(
                  'text-sm font-medium',
                  isError ? 'text-destructive' : 'text-muted-foreground'
                )}
              >
                No projects found
              </p>
            )}
          </div>
        )}
      </div>
      <div className='shrink-0'>
        {(sortedRows?.length ?? 0) > 0 && !isLoading && (
          <CustomPagination
            limit={limit}
            setLimit={setLimit}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            totalRecords={totalrecords}
            title='Projects'
          />
        )}
      </div>

      <ProjectModel
        isDialogOpen={
          ['finance'].includes(isDialogOpen || '') ? null : isDialogOpen
        }
        data={editData}
        setIsDialogOpen={() => {
          setIsDialogOpen(null)
          setEditData(null)
          setSelectedIndex(-1)
          pendingEdgeRef.current = null
        }}
        globalIndex={globalIndex}
        totalCount={totalRecords}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onPrev={handlePrevProject}
        onNext={handleNextProject}
      />

      <ProjectDetails
        open={isDialogOpen === 'finance'}
        data={editData}
        onClose={() => {
          setIsDialogOpen(null)
          setEditData(null)
        }}
      />

      <ProjectNotesDrawer
        projectId={notesDrawerProject?.project_id ?? null}
        projectName={notesDrawerProject?.list_name ?? null}
        open={notesDrawerProject !== null}
        onOpenChange={openState => {
          if (!openState) setNotesDrawerProject(null)
        }}
      />

      <SavedFiltersPanel
        open={isSavedFiltersPanelOpen}
        onClose={() => setIsSavedFiltersPanelOpen(false)}
        page='projects'
        currentFilters={currentFiltersSnapshot}
        onApply={applyPreset}
        startInSaveMode={savedFilterStartInSaveMode}
      />
    </div>
  )
}
